import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, throwError } from 'rxjs';
import { PaginationModel, RequestParamModel, ResourceCategoryModel, ResourceSubCategoryModel } from '@coreModels';
import { ApiConstants } from '@coreConstant';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ResourceService {
  constructor(private http: HttpClient) {}

  /**
   * Fetches the resource categories from the API.
   * It makes an HTTP GET request to the PRODUCT_RESOURCE_CATEGORY_LIST_URL with the given request parameters.
   * @param requestParamModel The request parameters to filter the resource categories.
   * @returns An observable containing the paginated list of resource categories.
   */
  getResourceCategory(requestParamModel: RequestParamModel): any {
    return this.http
      .get(ApiConstants.PRODUCT_RESOURCE_CATEGORY_LIST_URL + requestParamModel?.getParams(), { observe: 'response' })
      .pipe(
        map((response: any) => {
          const categoryList = new PaginationModel<any>();
          const headers = response.headers;
          categoryList.setHeaders(headers);
          categoryList.data = response.body.data as ResourceCategoryModel[];
          return categoryList;
        }),
        catchError((err: any) => {
          return throwError(() => err);
        })
      );
  }

  /**
   * Fetches the resource subcategories from the API based on the provided parameters.
   * It makes an HTTP GET request to the PRODUCT_RESOURCE_SUB_CATEGORY_LIST_URL with the given request parameters.
   * @param requestParamModel The request parameters to filter the resource subcategories.
   * @returns An observable containing the paginated list of resource subcategories.
   */
  getResourceSubCategory(requestParamModel: RequestParamModel): any {
    return this.http
      .get(ApiConstants.PRODUCT_RESOURCE_SUB_CATEGORY_LIST_URL + requestParamModel?.getParams(), { observe: 'response' })
      .pipe(
        map((response: any) => {
          const subcategoryList = new PaginationModel<any>();
          const headers = response.headers;
          subcategoryList.setHeaders(headers);
          subcategoryList.data = response.body.data as ResourceSubCategoryModel[];
          return subcategoryList;
        }),
        catchError((err: any) => {
          return throwError(() => err);
        })
      );
  }

  /**
   * Fetches the resources from the API based on the provided parameters.
   * It makes an HTTP GET request to the PRODUCT_RESOURCE_LIST_URL with the given request parameters.
   * @param requestParamModel The request parameters to filter the resources.
   * @returns An observable containing the paginated list of resources.
   */
  getResource(requestParamModel: RequestParamModel): any {
    return this.http
      .get(ApiConstants.PRODUCT_RESOURCE_LIST_URL + requestParamModel?.getParams(), { observe: 'response' })
      .pipe(
        map((response: any) => {
          const resourceList = new PaginationModel<any>();
          const headers = response.headers;
          resourceList.setHeaders(headers);
          resourceList.data = response.body.data as any[];
          return resourceList;
        }),
        catchError((err: any) => {
          return throwError(() => err);
        })
      );
  }

  /**
   * Initiates a download request for the resource based on the provided resource ID.
   * It calls the `baseService.getDownloadFileContent` method to download the file content.
   * @param body The body containing the resource ID.
   * @returns An observable for downloading the resource.
   */
  downloadResource(body: any) {
    return `${environment.API_URL}${ApiConstants.PRODUCT_RESOURCE_DOWNLOAD_URL}${body.id}`;
  }
}
